import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useLoaderData, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import DefaultApiService from "../../../../../infrastructure/api/default";
import useGetPageInfo from "../../../../common/hooks/useGetPageInfo";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import EnhancedTable from "../../../../components/shared/table";

const RejectedRequestsPage = () => {
  const pageInfo = useGetPageInfo();
  const dataList = useLoaderData("root");
  const navigate = useNavigate();

  const [restoreDialog, setRestoreDialog] = useState({
    open: false,
    row: null,
    reason: "",
  });
  const [busyId, setBusyId] = useState(null);

  const headCells = useMemo(
    () => [
      {
        id: "updateOn",
        label: "تاریخ رد",
        type: "date",
      },
      {
        id: "adminComment",
        label: "دلیل رد",
      },
      {
        id: "fullName",
        label: "متقاضی",
      },
      {
        id: "projectOwnerMobile",
        label: "موبایل",
      },
      {
        id: "referenceID",
        label: "شناسه درخواست",
      },
      {
        id: "projectTitle",
        label: "عنوان پروژه",
      },
      {
        id: "stepTitle",
        label: "مرحله فعلی",
      },
      {
        id: "requestConfirm",
        label: "وضعیت",
      },
    ],
    []
  );

  const rows = useMemo(
    () =>
      (Array.isArray(dataList) ? dataList : []).map((row) => ({
        ...row,
        fullName:
          row.fullName ||
          [row.projectOwnerFirstName, row.projectOwnerLastName].filter(Boolean).join(" ") ||
          "—",
        adminComment: row.adminComment || row.AdminComment || "—",
      })),
    [dataList]
  );

  const onFetchData = () => {
    navigate(".", { replace: true });
  };

  const handleRestore = async () => {
    const { row, reason } = restoreDialog;
    if (!row?.id) return;
    if (!String(reason || "").trim()) {
      toast.error("دلیل بازگشت به چرخه جاری الزامی است");
      return;
    }

    setBusyId(row.id);
    try {
      const res = await DefaultApiService.postApi(
        "SLN/Management/RejectedRequests/RestoreToActiveCycle",
        {
          requestId: row.id,
          reason: String(reason).trim(),
        }
      );
      if (res?.isSuccess === false) {
        toast.error(res?.message || "بازگشت به چرخه ناموفق بود");
        return;
      }
      toast.success(res?.message || "درخواست به چرخه جاری بازگشت");
      setRestoreDialog({ open: false, row: null, reason: "" });
      onFetchData();
    } catch (e) {
      toast.error(e?.message || "بازگشت به چرخه ناموفق بود");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12} md={12}>
        <Card>
          <CardHeader
            title={pageInfo.title || "درخواست‌های رد شده"}
            subheader="دلیل رد کارشناس و امکان بازگشت به چرخه جاری توسط مدیر"
            action={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "fit-content",
                }}
              >
                <IconButton variant="text" color="primary" onClick={onFetchData}>
                  <SyncIcon />
                </IconButton>
              </Box>
            }
          />

          <CardContent>
            <Grid container>
              <Grid item xs={12} md={12}>
                <EnhancedTable
                  rowKey={"id"}
                  rows={rows}
                  headCells={headCells}
                  defaultSort={{ orderBy: "updateOn", order: "desc" }}
                  pageSize={100}
                  actions={[
                    {
                      icon: <RestartAltIcon fontSize="small" />,
                      color: "success",
                      tooltip: "بازگشت به چرخه جاری",
                      hidden: (row) => busyId === row.id,
                      onClick: (row) =>
                        setRestoreDialog({ open: true, row, reason: "" }),
                    },
                  ]}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Dialog
        open={restoreDialog.open}
        onClose={() => setRestoreDialog({ open: false, row: null, reason: "" })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>بازگشت به چرخه جاری</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, mb: 2 }}>
            درخواست{" "}
            <strong>
              {restoreDialog.row?.referenceID || restoreDialog.row?.id || ""}
            </strong>{" "}
            به وضعیت جاری برمی‌گردد. دلیل بازگشت در لاگ و پیگیری‌ها ثبت می‌شود.
          </Box>
          {restoreDialog.row?.adminComment &&
          restoreDialog.row.adminComment !== "—" ? (
            <Box sx={{ mb: 2, color: "text.secondary", fontSize: "0.875rem" }}>
              دلیل رد قبلی: {restoreDialog.row.adminComment}
            </Box>
          ) : null}
          <TextField
            fullWidth
            label="دلیل بازگشت به چرخه"
            value={restoreDialog.reason}
            onChange={(e) =>
              setRestoreDialog((s) => ({ ...s, reason: e.target.value }))
            }
            multiline
            minRows={3}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRestoreDialog({ open: false, row: null, reason: "" })}
          >
            انصراف
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={!String(restoreDialog.reason || "").trim() || Boolean(busyId)}
            onClick={handleRestore}
          >
            تأیید بازگشت
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default RejectedRequestsPage;
